// react_frontend/src/Components/Modules/Report.js
import React, { useEffect, useState, useRef } from "react";
import Breadcrumb from "../Partials/Breadcrumb";
import { AxiosAuthInstance } from "../../AxiosInterceptors";

// RSuite Components
import {
  DateRangePicker,
  Button,
  Panel,
  Grid,
  Row,
  Col,
  Placeholder,
  Message,
  toaster,
  Tag,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// PDF Export
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

// Charts
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Icons
import {
  FaChartPie,
  FaChartBar,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaUsers,
  FaDownload,
  FaCalendarAlt,
  FaBox,
  FaExclamationTriangle,
  FaArrowDown,
  FaArrowUp,
} from "react-icons/fa";

const Report = () => {
  const [data, setData] = useState({});
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("BDT");
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [companyPackage, setCompanyPackage] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);

  const reportRef = useRef();

  const COLORS = ["#4351A3", "#5f6bb5", "#7a84c9", "#959ed2", "#b0b8da"];
  const STOCK_COLORS = ["#2E7D32", "#FFA726", "#EF5350"];

  // Format Currency with BDT symbol
  const formatCurrency = (value) => {
    if (value == null) value = 0;
    const num = parseFloat(value);
    if (currency === "BDT") {
      return `৳${num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "BDT",
      minimumFractionDigits: 2,
    }).format(num);
  };

  // Fetch Report Data
  const fetchReport = (range = null) => {
    setLoading(true);
    const payload = range
      ? { daterange: range.map((d) => d.toISOString().split("T")[0]) }
      : {};

    Promise.all([
      AxiosAuthInstance.post("reports", payload),
      AxiosAuthInstance.get("reports/inventory-summary")
    ])
      .then(([reportRes, invRes]) => {
        setData(reportRes.data);
        setInventoryData(invRes.data.inventory_summary || []);
      })
      .catch((err) => {
        console.error("Failed to fetch data:", err);
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to load report or inventory data.
          </Message>,
          { placement: "topEnd" }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDateRange = (range) => {
    if (range && range.length === 2) {
      setDateRange(range);
      fetchReport(range);
    }
  };

  // Export to PDF
  const exportPDF = () => {
    const input = reportRef.current;
    if (!input) return;

    html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`business_report_${new Date().toISOString().split("T")[0]}.pdf`);
    });
  };

  // Fetch Company Info
  useEffect(() => {
    setPackageLoading(true);
    AxiosAuthInstance.get("company/profile")
      .then((res) => {
        const company = res.data.company || {};
        setCurrency(company.currency || "BDT");
        setCompanyPackage(company.package || null);
      })
      .catch((err) => {
        console.error("Failed to fetch company profile:", err);
        setCompanyPackage(null);
      })
      .finally(() => {
        setPackageLoading(false);
      });
  }, []);

  // Fetch on Mount
  useEffect(() => {
    fetchReport();
  }, []);

  // === Calculations ===
  const totalEarnings =
    (parseFloat(data.total_sells) || 0) -
    (parseFloat(data.total_purchases) || 0) -
    (parseFloat(data.employee_salaries) || 0);

  // Stock Metrics
  const totalMainStock = inventoryData.reduce((sum, item) => sum + (item.main_stock || 0), 0);
  const totalUsedStock = inventoryData.reduce((sum, item) => sum + (item.used_qty || 0), 0);
  const totalAvailableStock = inventoryData.reduce((sum, item) => sum + (item.available_stock || 0), 0);
  const lowStockCount = inventoryData.filter(item => (item.available_stock || 0) < 10).length;

  // Category-wise Stock
  const categoryStockMap = {};
  inventoryData.forEach(item => {
    const cat = item.category?.category_name || "Uncategorized";
    if (!categoryStockMap[cat]) categoryStockMap[cat] = 0;
    categoryStockMap[cat] += item.available_stock || 0;
  });
  const categoryStockData = Object.entries(categoryStockMap).map(([name, stock]) => ({ name, stock }));

  return (
    <>
      <Breadcrumb title="Business Intelligence Report" />

      <Panel shaded bordered className="mt-3" bodyFill>
        <Grid fluid className="p-3">
          <Row className="align-items-center justify-content-between">
            <Col xs={24} md={12} className="mb-2 mb-md-0">
              <h5 className="text-dark mb-0 d-flex align-items-center">
                <FaFileInvoiceDollar className="me-2" />
                Business Performance & Inventory Report
              </h5>
            </Col>

            <Col xs={24} md={12} className="text-md-end">
              <div className="d-flex gap-2 justify-content-end flex-wrap">
                {!packageLoading && !["starter", "growth"].includes(companyPackage) && (
                  <>
                    <DateRangePicker
                      placeholder="Select Date Range"
                      value={dateRange}
                      onChange={handleDateRange}
                      style={{ width: 240 }}
                      size="md"
                      placement="bottomEnd"
                      cleanable
                      showOneCalendar
                    />
                    <Button
                      appearance="primary"
                      startIcon={<FaDownload />}
                      onClick={exportPDF}
                      disabled={loading}
                      title="Export full report as PDF"
                    >
                      Export PDF
                    </Button>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Grid>
      </Panel>

      {/* Report Content */}
      <div ref={reportRef} className="p-3 bg-white rounded shadow-sm">
        {/* === SALES & FINANCE SUMMARY === */}
        <h5 className="mb-4 pb-2 border-bottom">
          <FaMoneyBillWave className="me-2 text-primary" /> Financial Overview
        </h5>
        <Row gutter={16} className="mb-4">
          <Col xs={24} sm={12} md={5} className="mb-3">
            <Panel shaded bordered style={{ background: "linear-gradient(to right, #e3f2fd, #bbdefb)" }} bodyFill>
              <div className="p-3 text-center">
                <FaMoneyBillWave size={24} className="text-primary mb-2" />
                <h6 className="text-secondary mb-1">Total Sales</h6>
                <h4 className="text-dark fw-bold">{loading ? "..." : formatCurrency(data.total_sells)}</h4>
              </div>
            </Panel>
          </Col>

          <Col xs={24} sm={12} md={5} className="mb-3">
            <Panel shaded bordered style={{ background: "linear-gradient(to right, #f3e5f5, #ce93d8)" }} bodyFill>
              <div className="p-3 text-center">
                <FaMoneyBillWave size={24} className="text-purple mb-2" />
                <h6 className="text-secondary mb-1">Total Purchase</h6>
                <h4 className="text-dark fw-bold">{loading ? "..." : formatCurrency(data.total_purchases)}</h4>
              </div>
            </Panel>
          </Col>

          <Col xs={24} sm={12} md={5} className="mb-3">
            <Panel shaded bordered style={{ background: "linear-gradient(to right, #ffebee, #ef9a9a)" }} bodyFill>
              <div className="p-3 text-center">
                <FaMoneyBillWave size={24} className="text-danger mb-2" />
                <h6 className="text-secondary mb-1">Total Salary</h6>
                <h4 className="text-dark fw-bold">{loading ? "..." : formatCurrency(data.employee_salaries)}</h4>
              </div>
            </Panel>
          </Col>

          <Col xs={24} sm={12} md={5} className="mb-3">
            <Panel
              shaded
              bordered
              style={{
                background: totalEarnings >= 0 ? "linear-gradient(to right, #e8f5e8, #a5d6a7)" : "linear-gradient(to right, #ffebee, #ef9a9a)",
              }}
              bodyFill
            >
              <div className="p-3 text-center">
                <FaMoneyBillWave size={24} className={totalEarnings >= 0 ? "text-success mb-2" : "text-danger mb-2"} />
                <h6 className="text-secondary mb-1">Net Profit</h6>
                <h4 className={totalEarnings >= 0 ? "text-dark fw-bold" : "text-danger fw-bold"}>
                  {loading ? "..." : formatCurrency(totalEarnings)}
                </h4>
              </div>
            </Panel>
          </Col>

          <Col xs={24} sm={12} md={4} className="mb-3">
            <Panel shaded bordered style={{ background: "linear-gradient(to right, #fff3e0, #ffcc80)" }} bodyFill>
              <div className="p-3 text-center">
                <FaUsers size={24} className="text-warning mb-2" />
                <h6 className="text-secondary mb-1">Total Customers</h6>
                <h4 className="text-dark fw-bold">{loading ? "..." : data.total_customers || 0}</h4>
              </div>
            </Panel>
          </Col>
        </Row>

        {/* === INVENTORY SUMMARY === */}
        <h5 className="mb-4 pb-2 border-bottom mt-5">
          <FaBox className="me-2 text-info" /> Inventory Health
        </h5>
        <Row gutter={16} className="mb-4">
          <Col xs={24} sm={12} md={6} className="mb-3">
            <Panel shaded bordered style={{ background: "linear-gradient(to right, #e1f5fe, #b3e5fc)" }} bodyFill>
              <div className="p-3 text-center">
                <FaBox size={24} className="text-info mb-2" />
                <h6 className="text-secondary mb-1">Total Main Stock</h6>
                <h4 className="text-dark fw-bold">{loading ? "..." : totalMainStock.toLocaleString()}</h4>
              </div>
            </Panel>
          </Col>

          <Col xs={24} sm={12} md={6} className="mb-3">
            <Panel shaded bordered style={{ background: "linear-gradient(to right, #f1f8e9, #dcedc8)" }} bodyFill>
              <div className="p-3 text-center">
                <FaBox size={24} className="text-success mb-2" />
                <h6 className="text-secondary mb-1">Available For Stock</h6>
                <h4 className="text-dark fw-bold">{loading ? "..." : totalAvailableStock.toLocaleString()}</h4>
              </div>
            </Panel>
          </Col>

          <Col xs={24} sm={12} md={6} className="mb-3">
            <Panel shaded bordered style={{ background: "linear-gradient(to right, #fff8e1, #ffecb3)" }} bodyFill>
              <div className="p-3 text-center">
                <FaBox size={24} className="text-warning mb-2" />
                <h6 className="text-secondary mb-1">Selling Qty</h6>
                <h4 className="text-dark fw-bold">{loading ? "..." : totalUsedStock.toLocaleString()}</h4>
              </div>
            </Panel>
          </Col>

          <Col xs={24} sm={12} md={6} className="mb-3">
            <Panel
              shaded
              bordered
              style={{
                background: lowStockCount > 0 ? "linear-gradient(to right, #ffebee, #ffcdd2)" : "linear-gradient(to right, #e8f5e8, #c8e6c9)",
              }}
              bodyFill
            >
              <div className="p-3 text-center">
                <FaExclamationTriangle
                  size={24}
                  className={lowStockCount > 0 ? "text-danger mb-2" : "text-success mb-2"}
                />
                <h6 className="text-secondary mb-1">Low Stock Items</h6>
                <h4 className={lowStockCount > 0 ? "text-danger fw-bold" : "text-success fw-bold"}>
                  {loading ? "..." : lowStockCount}
                </h4>
                {lowStockCount > 0 && (
                  <Tag color="red" className="mt-2">
                    Restock Needed!
                  </Tag>
                )}
              </div>
            </Panel>
          </Col>
        </Row>

        {/* === CHARTS === */}
        <div className="row g-4 mt-4">
          {/* Payment Methods */}
          <div className="col-lg-6">
            <Panel
              header={
                <div className="d-flex align-items-center gap-2">
                  <FaChartPie /> Payment Methods
                </div>
              }
              bordered
              shaded
              bodyFill
            >
              {loading ? (
                <Placeholder.Paragraph rows={6} />
              ) : data.payment_methods?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.payment_methods}
                      dataKey="total"
                      nameKey="payment_method"
                      outerRadius={100}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {data.payment_methods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip formatter={(value) => [value, "Transactions"]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaChartPie size={48} className="mb-3 opacity-50" />
                  <p>No payment data</p>
                </div>
              )}
            </Panel>
          </div>

          {/* Category-wise Stock */}
          <div className="col-lg-6">
            <Panel
              header={
                <div className="d-flex align-items-center gap-2">
                  <FaChartBar /> Available Stock by Category
                </div>
              }
              bordered
              shaded
              bodyFill
            >
              {loading ? (
                <Placeholder.Paragraph rows={6} />
              ) : categoryStockData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={categoryStockData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} />
                    <YAxis />
                    <ReTooltip formatter={(value) => [value.toLocaleString(), "Units"]} />
                    <Legend />
                    <Bar dataKey="stock" fill="#0288D1" radius={[4, 4, 0, 0]} name="Available Stock" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaChartBar size={48} className="mb-3 opacity-50" />
                  <p>No stock data by category</p>
                </div>
              )}
            </Panel>
          </div>

          {/* Daily Breakdown */}
          <div className="col-12">
            <Panel
              header={
                <div className="d-flex align-items-center gap-2">
                  <FaCalendarAlt /> Daily Sales Breakdown
                </div>
              }
              bordered
              shaded
              bodyFill
            >
              {loading ? (
                <Placeholder.Paragraph rows={5} />
              ) : data.daily_breakdown?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Sales</th>
                        <th>VAT</th>
                        <th>Discount</th>
                        <th>Points Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.daily_breakdown.map((row, i) => (
                        <tr key={i}>
                          <td>{row.date}</td>
                          <td>{formatCurrency(row.sell)}</td>
                          <td>{formatCurrency(row.vat)}</td>
                          <td>{formatCurrency(row.discount)}</td>
                          <td>{row.point || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaCalendarAlt size={48} className="mb-3 opacity-50" />
                  <p>No daily data</p>
                </div>
              )}
            </Panel>
          </div>

          {/* Low Stock Items Table */}
          {lowStockCount > 0 && (
            <div className="col-12">
              <Panel
                header={
                  <div className="d-flex align-items-center gap-2 text-danger">
                    <FaExclamationTriangle /> Low Stock Items (Below 10 Units)
                  </div>
                }
                bordered
                shaded
                bodyFill
              >
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Item</th>
                        <th>Category</th>
                        <th>Main Stock</th>
                        <th>Used</th>
                        <th>Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryData
                        .filter(item => (item.available_stock || 0) < 10)
                        .map((item, i) => (
                          <tr key={i} className="table-danger">
                            <td>{item.item?.item_name || "N/A"}</td>
                            <td>{item.category?.category_name || "N/A"}</td>
                            <td>{item.main_stock || 0}</td>
                            <td>{item.used_qty || 0}</td>
                            <td><strong>{item.available_stock || 0}</strong></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Report;